// @ts-check

/**
 * @file Renders the timelog edit screen.
 */

import {Form} from "@kaspernj/api-maker/build/form"
import Text from "@kaspernj/api-maker/build/utils/text"
import {useLocalSearchParams, useRouter} from "expo-router"
import {createRef, memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import Button from "../../components/button"
import buildRoutePath from "../../shared/build-route-path"
import Card from "../../components/card"
import errorMessage from "../../shared/error-message"
import FormDurationInput from "../../components/form/form-duration-input"
import FormTextInput from "../../components/form/form-text-input"
import TasksSelect from "../../components/tasks/select"
import {queueOfflineTimelogCreate, queueOfflineTimelogEdit} from "../../database/project-replica-timelog-commands"
import {Timelog} from "../../frontend-models/timelog"
import {durationSecondsToInput, parseDurationSeconds} from "../../shared/duration"
import {optionalRouteParam} from "../../shared/route-params"
import useCurrentUser from "../../auth/use-current-user"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"
import {useScreenLoading} from "../../components/board-layout/screen-loading"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @import {OfflineTimelogChanges} from "../../database/project-replica-timelog-commands.js" */
/** @import {OpenProjectReplica} from "../../database/project-replica.js" */
/** @import {default as LocalTimelogClass} from "../../models/timelog.js" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} TimelogsEditScreenProps
 * @property {boolean} [online] Whether online-only commands are available.
 * @property {string | null} [prefilledTaskID] Task id used when adding from a task modal.
 * @property {import("../../database/project-replica.js").OpenProjectReplica | null} [replica] Open project replica enabling offline timelog entry.
 * @property {string | null} [projectId] Owning project replica id.
 * @property {Timelog | null} [timelog] Existing timelog supplied by an embedded editor.
 * @property {((timelog: Timelog) => Promise<void> | void) | null} [onSaved] Callback for an embedded editor; route navigation is skipped.
 */

/**
 * @typedef {object} TimelogFormAttributes
 * @property {string} comment Free-text work note.
 * @property {string} date Work date (YYYY-MM-DD).
 * @property {string} taskId Owning task UUID.
 * @property {number} timeSeconds Worked duration in seconds.
 * @property {number} timeTransportSeconds Transport duration in seconds.
 * @property {number} transportLength Kilometres travelled on the trip.
 */

/**
 * @typedef {object} TimelogsEditScreenState
 * @property {boolean} loading Whether the timelog payload is loading.
 * @property {boolean} saving Whether the save action is running.
 * @property {{kind: "error" | "success", text: string} | null} status Status message shown after load or save failures.
 * @property {Timelog | null} timelog Existing timelog model, or null for a new timelog.
 */

/**
 * Timelog edit screen.
 * @augments {ShapeComponent<TimelogsEditScreenProps, TimelogsEditScreenState>}
 */
class TimelogsEditScreen extends ShapeComponent {
  static defaultProps = {
    onSaved: null,
    online: true,
    prefilledTaskID: null,
    projectId: null,
    replica: null,
    timelog: null
  }

  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {string | null} */
  prefilledTaskID
  /** @type {string | null} */
  timelogID
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {ReturnType<typeof useCurrentUser>} */
  auth = this.hookField()
  formObjectRef = createRef()
  /** @type {TimelogsEditScreenState} */
  state = {
    loading: true,
    saving: false,
    status:
      /** @type {{kind: "error" | "success", text: string} | null} */ null,
    timelog: /** @type {Timelog | null} */ null
  }

  /**
   * Initializes route state and loads the timelog being edited.
   * @returns {void} No return value.
   */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params = useLocalSearchParams()
    const router = useRouter()

    const routePrefilledTaskID = optionalRouteParam(params, "task_id", "Task ID")
    const routeTimelogID = optionalRouteParam(params, "id", "Timelog ID")
    const embeddedTimelog = this.p.timelog || null
    const prefilledTaskID = this.p.prefilledTaskID || routePrefilledTaskID
    const timelogID = embeddedTimelog ? String(embeddedTimelog.id()) : routeTimelogID
    this.params = params
    this.prefilledTaskID = prefilledTaskID
    this.router = router
    this.timelogID = timelogID
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    this.auth = useCurrentUser()
    useEffect(() => {
      let cancelled = false
      const loadData = async () => {
        try {
          const timelog = embeddedTimelog || await (timelogID ? Timelog.find(timelogID) : null)
          if (cancelled) return
          this.s.loading = false
          this.s.timelog = timelog
        } catch (error) {
          if (cancelled) return
          this.s.loading = false
          this.s.status = {
            kind: "error",
            text: errorMessage(error)
          }
        }
      }
      void loadData()
      return () => {
        cancelled = true
      }
    }, [embeddedTimelog, timelogID])
  }

  /**
   * Renders the timelog form.
   * @returns {ReactNode} Timelog edit screen content.
   */
  render() {
    const {loading, saving, status, timelog} = this.s
    const {prefilledTaskID, timelogID} = this.tt
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="timelogEditScreen"
        />
      )
    }
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="timelogEditScreen"
      >
        <Text
          style={styles[`screenTitle-${this.tt.themeName}`] ||= {
            fontSize: 24,
            fontWeight: 700,
            marginBottom: 16
          }}
          testID="timelogEditScreen/title"
        >
          {timelogID ? _("Edit timelog") : _("Add timelog")}
        </Text>
        <Form formObjectRef={this.formObjectRef} onSubmit={this.tt.onSubmit}>
          <Card
            style={styles.formCardSpacing ||= {
              gap: 12,
              maxWidth: 520,
              width: "100%"
            }}
            testID="timelogEditScreen/form"
          >
            <TasksSelect
              defaultValue={timelog ? timelog.taskId() : prefilledTaskID || ""}
              includeProjectName
              label={_("Task")}
              name="task_id"
              online={this.p.online}
              projectId={this.p.projectId}
              replica={this.p.replica}
              testID="timelogEditScreen/taskSelect"
            />
            <FormTextInput
              defaultValue={
                timelog
                  ? formatDate(timelog.date())
                  : new Date().toISOString().slice(0, 10)
              }
              label={_("Date")}
              name="date"
              testID="timelogEditScreen/dateInput"
            />
            <FormDurationInput
              defaultValue={
                timelog
                  ? durationSecondsToInput(timelog.timeSeconds())
                  : ""
              }
              label={_("Time")}
              name="time"
              testID="timelogEditScreen/timeInput"
            />
            <FormDurationInput
              defaultValue={
                timelog
                  ? durationSecondsToInput(timelog.timeTransportSeconds())
                  : ""
              }
              label={_("Time transport")}
              name="time_transport"
              testID="timelogEditScreen/timeTransportInput"
            />
            <FormTextInput
              defaultValue={
                timelog ? String(timelog.transportLength() || "") : ""
              }
              keyboardType="numeric"
              label={_("Transport length")}
              name="transport_length"
              testID="timelogEditScreen/transportLengthInput"
            />
            <FormTextInput
              defaultValue={timelog ? timelog.comment() || "" : ""}
              label={_("Comment")}
              multiline
              name="comment"
              testID="timelogEditScreen/commentInput"
            />
            {status &&
              <Text
                style={styles[`status-${this.tt.themeName}-${status.kind}`] ||= {
                  color: status.kind === "success" ? this.tt.colors.successText : this.tt.colors.dangerText,
                  fontWeight: 600
                }}
                testID="timelogEditScreen/statusMessage"
              >
                {status.text}
              </Text>
            }
            <Button
              disabled={saving}
              iconName="floppy-disk"
              label={saving ? _("Saving...") : _("Save")}
              onPress={this.tt.onSubmit}
              testID="timelogEditScreen/saveButton"
            />
          </Card>
        </Form>
      </View>
    )
  }

  /**
   * Saves a new or existing timelog.
   * @returns {Promise<void>} Resolves after save handling finishes.
   */
  onSubmit = async () => {
    const {params, router} = this.tt
    const {timelog} = this.s
    const formObject = this.formObjectRef.current
    if (!formObject) return
    const values = formObject.asObject()
    const timeSeconds = parseDurationSeconds(String(values.time || ""))
    const timeTransportInput = String(values.time_transport || "").trim()
    const timeTransportSeconds = timeTransportInput
      ? parseDurationSeconds(timeTransportInput)
      : 0

    if (!timeSeconds || timeSeconds <= 0 || timeTransportSeconds == null) {
      this.s.status = {
        kind: "error",
        text: _("Enter a duration like 1h 5m, 30s or 1.5")
      }
      return
    }
    const attributes = {
      comment: values.comment || "",
      date: values.date || "",
      taskId: values.task_id || "",
      timeSeconds,
      timeTransportSeconds,
      transportLength: parseInt(values.transport_length, 10) || 0
    }
    this.s.saving = true
    this.s.status = null
    const offline = !this.p.online && this.p.replica
    try {
      if (timelog) {
        if (offline) {
          const {timelog: updated} = await this.saveOffline(attributes)
          if (this.p.onSaved) {
            // The embedded editor only uses this to close and re-read the list from the replica,
            // so the local row satisfies the callback's shape.
            await this.p.onSaved(/** @type {Timelog} */ (/** @type {unknown} */ (updated)))
          }
          return
        }
        await timelog.update(attributes)
        if (this.p.onSaved) {
          await this.p.onSaved(timelog)
        } else {
          router.push(buildRoutePath(params, `/timelogs/${timelog.id()}`))
        }
      } else {
        const {user} = this.tt.auth
        if (!user) throw new Error("Expected a signed-in user when creating a timelog")
        if (offline) {
          const {timelog: createdOffline} = await this.saveOffline(attributes)
          if (this.p.onSaved) {
            // The embedded editor only uses this to close and re-read the list from the replica.
            await this.p.onSaved(/** @type {Timelog} */ (/** @type {unknown} */ (createdOffline)))
          } else {
            router.push(buildRoutePath(params, `/timelogs/${createdOffline.id()}`))
          }
          return
        }
        const created = await Timelog.create({...attributes, userId: String(user.id)})
        if (this.p.onSaved) {
          await this.p.onSaved(created)
        } else {
          router.push(buildRoutePath(params, `/timelogs/${created.id()}`))
        }
      }
    } catch (error) {
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    } finally {
      this.s.saving = false
    }
  }

  /**
   * Saves a new or existing timelog offline, writing the local replica row and
   * queueing a replay intent for the coordinator. Invoiced rows and invalid
   * duration/date inputs are refused by the offline command layer.
   * @param {TimelogFormAttributes} attributes Normalized form attributes.
   * @returns {Promise<{timelog: InstanceType<typeof LocalTimelogClass>}>} The persisted local row.
   * @rejects {Error} When the replica is unusable or validation fails.
   */
  saveOffline = async (attributes) => {
    const replica = this.p.replica
    if (!replica) throw new Error("Offline timelog saving requires an open project replica")
    if (this.s.timelog) {
      /** @type {OfflineTimelogChanges} */
      const changes = {
        timeSeconds: attributes.timeSeconds,
        timeTransportSeconds: attributes.timeTransportSeconds,
        transportLength: attributes.transportLength
      }
      if (attributes.comment !== undefined) changes.comment = attributes.comment
      if (attributes.date) changes.date = attributes.date
      if (attributes.taskId) changes.taskId = attributes.taskId
      const updated = await queueOfflineTimelogEdit({changes, replica, timelogId: String(this.s.timelog.id())})
      return {timelog: updated}
    } else {
      const {user} = this.tt.auth
      if (!user) throw new Error("Expected a signed-in user when creating a timelog")
      const {timelog} = await queueOfflineTimelogCreate({
        comment: attributes.comment,
        date: attributes.date,
        replica,
        taskId: String(attributes.taskId),
        timeSeconds: attributes.timeSeconds,
        timeTransportSeconds: attributes.timeTransportSeconds,
        transportLength: attributes.transportLength,
        userId: String(user.id)
      })
      return {timelog}
    }
  }
}

/** @type {NamedExoticComponent<TimelogsEditScreenProps>} */
const TimelogsEditScreenComponent = memo(shapeComponent(TimelogsEditScreen))

export default TimelogsEditScreenComponent

/**
 * Formats a date value for date input defaults.
 * @param {Date | string | null | undefined} value Date-like value from a timelog.
 * @returns {string} Date string in `YYYY-MM-DD` format.
 */
function formatDate(value) {
  if (value instanceof Date) return value.toISOString().slice(0, 10)
  if (typeof value === "string") return value.slice(0, 10)
  return new Date().toISOString().slice(0, 10)
}
